import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Category } from '../../models/Category'
import { RecordFactory } from '../../models/RecordFactory'
import { okResult } from '../../types/utils'
import { navigateToNextDefaultStep } from '../../utils/defaults'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { CategoriesPage } from '../items/CategoriesPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'

export function SetupDefaultCategoriesPage() {
    const me = PSApplication.me()
    const owner = PSApplication.owner()
    const navigate = usePSNavigate()
    const editionID = getEditionID(me)
    const { logAndDisplayError } = useErrorHandler()

    const queryClient = useQueryClient()
    const { data, isLoading } = useQuery({
        queryKey: ['defaults', 'categories', editionID],
        queryFn: async () => {
            const [edition, categories, componentItems] = await Promise.all([
                RecordFactory.getInstance().loadEdition(getEditionID(me)),
                RecordFactory.getInstance().loadCategories(owner.id!, ['service', 'product']),
                RecordFactory.getInstance().loadItems(owner.id!),
            ])

            const installedCategoriesFromSameEdition = categories.filter(
                (c) => c.edition === editionID
            )

            const categoriesToSelect = (edition?.defaultCategories ?? [])
                .filter(
                    (c) =>
                        installedCategoriesFromSameEdition.find(
                            (saved) => saved.editionCategory === c.id
                        ) === undefined
                )
                .map((c) => ({ category: c, selected: false }))

            return {
                edition,
                categories: categoriesToSelect,
                componentItems,
            }
        },
    })

    const onNextHandler = async (categories: Category[]) => {
        // Send the categories with the proper IDs set
        const clonedCategories = categories.map((c) => {
            const clone = c.clone()
            clone.listings = []
            clone.edition = editionID
            clone.editionCategory = c.id
            clone.id = undefined
            return clone
        })

        const result = await RecordFactory.getInstance().sendOnboardingCategories(
            clonedCategories,
            editionID,
            false
        )

        if (!result.ok) {
            logAndDisplayError(result.error)
            return result
        }

        queryClient.invalidateQueries({
            queryKey: ['defaults'],
        })

        const initialStep = 'categories'

        await navigateToNextDefaultStep(navigate, {
            edition: data?.edition!,
            potentialItemIDs: categories.flatMap((c) => c.listings),
            step: initialStep,
            initialStep, // if we start from categories, it's always the initial step
        })

        return okResult(null)
    }

    if (isLoading || !data) {
        return (
            <WorkflowSelectListSkeleton
                title="Categories"
                subtitle="This is a set of suggested categories to organize your services and products.
                    Select and customize them for your business"
                onClose={() => {}}
            />
        )
    }

    return (
        <CategoriesPage
            categories={data.categories}
            onSave={onNextHandler}
            forOnboarding={false}
            componentItems={data.componentItems}
            onBack={{
                title: 'Categories',
                onClick: () => navigate('/settings/categories'),
            }}
            onClose={() => navigate('/settings/categories')}
        />
    )
}
